import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { Configuration } from 'src/app/models/configurators-2d/applications/kitchen/configuration';
import { BaseAppService } from '../base-app-service';
import { StorageConfigurationData } from '../../../stores/tunnel/model/configuration-data/storage-configuration-data';
import { TunnelDataStore } from '../../../stores/tunnel/tunnel-data.store';
import { Subscription } from 'rxjs';
import { StorageTunnelData } from '../../../models/configurators-2d/applications/storage/storage-tunnel-data';
import {
  StorageCreateUpdateTunnel,
  StorageConfiguration,
  StorageInspiration,
  BaseStorageConfigurationDto,
  StorageInspirationDto,
  StorageTunnelPricingDto,
  StorageInstallationDto,
  StorageTunnelConceptionDetailPrice,
} from 'src/app/models/configurators-2d/tunnel/storage/storage-tunnel-dtos';
import {
  DuplicateTunnelHBMInput,
  GetTunnelByHBMUUIDInput,
  HbmPdfViewModel,
} from 'src/app/models/configurators-2d/tunnel/common-hbm-tunnel-dtos';
import { ApplyDiscountInput } from 'src/app/models/configurators-2d/tunnel/apply-discount-input';
import { DeleteTunnelInput } from 'src/app/models/configurators-2d/tunnel/delete-tunnel-input';
import { FundingLegalInformation } from 'src/app/models/configurators-2d/tunnel/funding-legal-information';
import { GetFundingDataInput } from 'src/app/models/configurators-2d/tunnel/get-funding-data-input';
import { GetFundingLegalInformationInput } from 'src/app/models/configurators-2d/tunnel/get-funding-legal-information-input';
import { GetInspirationInput } from 'src/app/models/configurators-2d/tunnel/get-inspiration-input';
import { GetInstallationDataInput } from 'src/app/models/configurators-2d/tunnel/get-installation-data-input';
import { GetPricingInput } from 'src/app/models/configurators-2d/tunnel/get-pricing-input';
import { TunnelUpdateInstallation } from 'src/app/models/configurators-2d/tunnel/tunnel-update-installation';
import { UpdateInstallationInput } from 'src/app/models/configurators-2d/tunnel/update-installation-input';
import { FundingState } from 'src/app/stores/tunnel/model/funding-state';
import { InstallationState } from 'src/app/stores/tunnel/model/installation-state';
import { Price } from 'src/app/stores/tunnel/model/price';
import { PricingState } from 'src/app/stores/tunnel/model/pricing-state';
import { DiscountTypeEnum } from 'src/app/enum/discount-type.enum';
import { Customer } from '../../kingfisher/customer';
import { GetCustomerFidelityInfoNouveauPointInput } from '../../fidelity/fidelity';
import { FidelityService } from '../../fidelity/fidelity.service';
import { ProjectService } from '../../project.service';
import { HBMMedia, HBMMedias } from '../../../models/configurators-2d/applications/kitchen/hbm-media';
import { logError } from '../../helpers/sentry.helper';
import { FeatureSettingsService } from '../../feature-settings/feature-settings.service';
import { BomDataHelper } from '../../helpers/bom-data-helper';
import { FundingRehydrationService } from '../funding-rehydration.service';

@Injectable({
  providedIn: 'root',
})
export class StorageService extends BaseAppService<Configuration> {
  private readonly fundingRehydration = inject(FundingRehydrationService);
  private currentStorageConfiguration: StorageConfigurationData;
  private currentInstallation: InstallationState;
  private currentPricing: PricingState;
  private currentFunding: FundingState;
  private currentCustomer: Customer;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly fidelityService: FidelityService,
    private readonly featureSettingsService: FeatureSettingsService,
    private readonly tunnelDataStore: TunnelDataStore,
    private readonly projectService: ProjectService
  ) {
    super(CONFIG_CONTEXT.STORAGE, Configuration);
    this.subscribeToObservable();
  }

  private map(data: StorageConfigurationData): Partial<StorageTunnelData> {
    return {
      ...(data.HBMConfigurationId && { projectUUID: data.HBMConfigurationId }),
      ...(data.projectTitle && { title: data.projectTitle }),
      ...(this.customer.userDatas?.id && { customerId: this.customer.userDatas?.id }),
      ...(this.current?.vendor?.sapId && { creatorId: this.current.vendor.sapId }),
      ...(data.bom && { bom: data.bom }),
      ...(data.configurationId && { id: data.configurationId }),
    };
  }

  private subscribeToObservable() {
    this.subscriptions.push(
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.currentStorageConfiguration = configurationData.get(CONFIG_CONTEXT.STORAGE) as StorageConfigurationData;
        }
      }),
      this.tunnelDataStore.getCurrentInstallation().subscribe(async installation => {
        if (installation) {
          this.currentInstallation = installation;
        }
      }),
      this.tunnelDataStore.getCurrentFunding().subscribe(funding => {
        if (funding) {
          this.currentFunding = funding;
        }
      }),
      this.tunnelDataStore.getCurrentPricing().subscribe(pricing => {
        if (pricing) {
          this.currentPricing = pricing;
        }
      }),
      this.tunnelDataStore.getCurrentCustomer().subscribe(customer => {
        if (customer) {
          this.currentCustomer = customer.userData;
        }
      })
    );
  }

  /**
   * Consentement déjà enregistré pour ce projet, si connu. Lit d'abord l'état du tunnel Storage
   * ({@link currentStorageConfiguration}, jamais alimenté par {@link BaseAppService.currentConfiguration}
   * pour ce flux HBM) ; si ce projet vient d'être rouvert et que la valeur n'a pas encore été
   * chargée en mémoire cette session, va la chercher en base et la met en cache.
   */
  protected override async getPersistedContactedByPhone(configId: string): Promise<boolean | undefined> {
    if (this.currentStorageConfiguration?.storageConfigurationId !== configId) {
      return undefined;
    }
    if (this.currentStorageConfiguration.contactedByPhone !== undefined) {
      return this.currentStorageConfiguration.contactedByPhone;
    }

    const persisted = await this.fetchContactedByPhone(configId);
    this.currentStorageConfiguration.contactedByPhone = persisted;
    return persisted;
  }

  /**
   * Mémorise le consentement résolu dans l'état du tunnel Storage, pour qu'un prochain envoi
   * dans la même session (ex. après déverrouillage) le réutilise au lieu de rouvrir la pop-up.
   */
  protected override setPersistedContactedByPhone(configId: string, contactedByPhone: boolean): void {
    if (this.currentStorageConfiguration?.storageConfigurationId === configId) {
      this.currentStorageConfiguration.contactedByPhone = contactedByPhone;
    }
  }

  protected override get currentConfigurationIdForContactedByPhone(): string | undefined {
    return this.currentStorageConfiguration?.storageConfigurationId;
  }

  public async createStorage(initialData: StorageConfigurationData) {
    initialData.inspiration =
      this.currentStorageConfiguration?.inspiration ??
      ({
        inspirationId: null,
        legsId: null,
        frontId: null,
        boxId: null,
        handlesId: null,
      } as StorageInspiration);

    const currentConfigurationId = this.currentStorageConfiguration.configurationId;
    const currentStorageConfigurationId = this.currentStorageConfiguration.storageConfigurationId;

    let resultCreate = null;
    if (currentConfigurationId) {
      await this.updateStorageConfiguration({
        ...initialData,
        configurationId: currentConfigurationId,
      });
    } else {
      initialData.inspiration = Object.assign(new StorageInspiration(), initialData.inspiration || {});
      resultCreate = await this.createStorageTunnel({
        bom: initialData.bom,
        uuidhbm: initialData.HBMConfigurationId,
        storageInspiration: initialData.inspiration.toStorageInspirationDto(),
        title: initialData.projectTitle,
      });
    }

    this.tunnelDataStore.resetCurrentInstallation();
    this.tunnelDataStore.resetCurrentPricing();
    this.tunnelDataStore.resetCurrentFunding();

    this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.STORAGE, {
      ...initialData,
      configurationId: currentConfigurationId ?? resultCreate.id,
      storageConfigurationId: currentStorageConfigurationId ?? resultCreate.storageConfigurationId,
    } as StorageConfigurationData);
  }

  public async updateStorageConfiguration(data: StorageConfigurationData, withReset: boolean = true) {
    data = {
      ...this.currentStorageConfiguration,
      ...data,
    };
    this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.STORAGE, data);
    data.inspiration = Object.assign(new StorageInspiration(), data.inspiration || {});

    const inputRequest = {
      tunnelConfigurationId: data.configurationId,
      title: this.current.HBMProject.name,
      bom: data.bom,
      uuidhbm: data.HBMConfigurationId ?? this.current.HBMProject.uuid,
      storageInspiration: data.inspiration?.toStorageInspirationDto(),
    };

    if (this.currentStorageConfiguration.configurationId == null) {
      const resultCreate = await this.createStorageTunnel(inputRequest);

      this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.STORAGE, {
        configurationId: resultCreate.id,
        storageConfigurationId: resultCreate.storageConfigurationId,
        projectTitle: this.current.HBMProject.name,
        inspiration: data.inspiration,
      } as StorageConfigurationData);
    } else {
      await this.updateStorageTunnel(inputRequest);
    }

    if (withReset) {
      this.tunnelDataStore.resetCurrentInstallation();
      this.tunnelDataStore.resetCurrentPricing();
      this.tunnelDataStore.resetCurrentFunding();
      this.tunnelDataStore.updateCurrentDiscount(DiscountTypeEnum.Discount0);
    }
  }

  public async getLiteStorageConfiguration(HBMConfigurationId: string): Promise<BaseStorageConfigurationDto> {
    return await this.getStorageTunnelByUUID({
      hbmUUID: HBMConfigurationId,
    });
  }

  public async loadStorageConfigurationFromHBM(HBMConfigurationId: string): Promise<BaseStorageConfigurationDto> {
    //le GUID est sensible à la casse, il faut le mettre en majuscule
    HBMConfigurationId = HBMConfigurationId.toUpperCase();
    let result;
    result = this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.STORAGE, {
      configurationId: null,
      storageConfigurationId: null,
      projectTitle: null,
      bom: null,
      inspiration: null,

      HBMConfigurationId: HBMConfigurationId,
    } as StorageConfigurationData);

    try {
      result = await this.getStorageTunnelByUUID({
        hbmUUID: HBMConfigurationId,
      });
      this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.STORAGE, {
        configurationId: result?.id,
        storageConfigurationId: result?.storageConfigurationId,
        HBMConfigurationId: HBMConfigurationId,
        projectTitle: result?.title,
        bom: result?.bom,
        inspiration: await this.initStorageInspiration(result?.id),
        medias: await this.getStorageMedias(HBMConfigurationId),
      } as StorageConfigurationData);
      if (result?.id && !BomDataHelper.isBomUnavailableForProductList(result?.bom)) {
        await this.loadStoragePricing(result.id);
        // Filet : `Tunnel/pricing` ne ramène pas toujours les offres persistées.
        await this.fundingRehydration.rehydrateIfNeeded(result.id, this);
        await this.computeFidelity();
      }
    } catch { }

    return result;
  }

  protected async fidelityEnabled(): Promise<boolean> {
    return this.featureSettingsService.isFidelityEnabled();
  }

  private async computeFidelity() {
    if (!(await this.fidelityEnabled())) return;
    if (!this.currentPricing?.totalWithoutFunding?.actualAmount) return;
    await this.computeFidelityPoint({
      amount: this.currentPricing.totalWithoutFunding?.actualAmount,
      customerId: this.currentCustomer.kfId,
    });
  }

  public async computeInstallation() {
    const poseEnabled = await this.featureSettingsService.isPoseEnabled();

    if (!poseEnabled) {
      this.tunnelDataStore.disableCurrentInstallation();
      return;
    }

    try {
      this.tunnelDataStore.setInstallationLoading(true);
      let result = await this.getInstallationData({
        tunnelConfigurationId: this.currentStorageConfiguration.configurationId,
        houseMoreThan2YearsOld: this.currentInstallation?.more2Years,
      });
      this.tunnelDataStore.resetCurrentPricing();
      this.tunnelDataStore.resetCurrentFunding();

      this.tunnelDataStore.updateCurrentInstallation({
        more2Years: result.houseMoreThan2YearsOld,
        withInstallation: result.withInstallation,
        detailInstallation: JSON.parse(result.installationPrice?.installationDetails),
      });

      this.loadPricingFromInstallation(result);
    } catch (error) {
      logError(error);
    } finally {
      this.tunnelDataStore.setInstallationLoading(false);
    }
  }

  public async updateDiscount(input: ApplyDiscountInput) {
    this.tunnelDataStore.setPricingLoading(true);
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();
    const discountInput = {
      ...input,
      requiresFinancingRecalculation: financementEnabled && input.requiresFinancingRecalculation,
    };
    let res = await this.applyDiscount(discountInput);
    await this.updateStoragePricingFromKitchenTunnelPricing(res);
    await this.computeFidelity();
    this.tunnelDataStore.updateCurrentDiscount(discountInput.discountType);
    this.tunnelDataStore.setPricingLoading(false);
  }

  public async updateWithInstallation(withInstallation: boolean) {
    const poseEnabled = await this.featureSettingsService.isPoseEnabled();

    if (!poseEnabled) {
      this.tunnelDataStore.disableCurrentInstallation();
      return;
    }

    await this.updateInstallation({
      tunnelConfigurationId: this.currentStorageConfiguration.configurationId,
      installationUpdate: {
        withInstallation: withInstallation,
      },
    });
  }

  public async deleteStorage(input: DeleteTunnelInput) {
    return await this.deleteTunnel(input);
  }

  public async duplicateStorage(input: DuplicateTunnelHBMInput) {
    return await this.duplicateHbmTunnel(input);
  }

  public async loadStoragePricing(tunnelConfigurationId: string) {
    try {
      await this.computeStoragePricing({
        tunnelConfigurationId: tunnelConfigurationId,
        shouldRecalculate: false,
      });
    } catch (error) {
      logError(error);
    }
  }

  public async computeFunding(input: GetFundingDataInput) {
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();

    if (!financementEnabled) {
      // Financement coupé : on recharge quand même le pricing pour que les totaux suivent
      // le scénario de pose courant (le mapper de pricing repasse par disableCurrentFunding).
      this.tunnelDataStore.disableCurrentFunding();
      await this.loadStoragePricing(input.tunnelConfigurationId);
      return;
    }

    try {
      this.tunnelDataStore.setFundingLoading(true);
      let result = await this.getFundingData(input);
      await this.updatePricing(result);
    } catch (error) {
      logError(error);
    } finally {
      this.tunnelDataStore.setFundingLoading(false);
    }
  }

  public async selectOffer(code: string, duration: number, other: boolean = false) {
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();

    if (!financementEnabled) {
      this.tunnelDataStore.disableCurrentFunding();
      return;
    }

    await this.getFundingLegalInformation({
      tunnelConfigurationId: this.currentStorageConfiguration.configurationId,
      selectedBareme: code,
      financingDuration: duration,
    });
    await this.loadStoragePricing(this.currentStorageConfiguration.configurationId);
  }

  private async updatePricing(pricing?: StorageTunnelPricingDto) {
    if (pricing) {
      await this.updateStoragePricingFromKitchenTunnelPricing(pricing);
    }
  }

  private getFunding(other: boolean) {
    const shouldUseFundingWithoutInstallation = other
      ? this.currentInstallation.withInstallation
      : !this.currentInstallation.withInstallation;
    return this.currentFunding[
      shouldUseFundingWithoutInstallation ? 'fundingWithoutInstallation' : 'fundingWithInstallation'
    ];
  }

  private async computeStoragePricing(input: GetPricingInput) {
    try {
      let result: StorageTunnelPricingDto = await this.getPricing(input);
      this.tunnelDataStore.setPricingLoading(true);
      await this.updateStoragePricingFromKitchenTunnelPricing(result);
    } catch (error) {
      logError(error);
    } finally {
      this.tunnelDataStore.setPricingLoading(false);
    }
  }

  private getFundingLegalInformation(input: GetFundingLegalInformationInput): Promise<FundingLegalInformation> {
    return this.getHbm<FundingLegalInformation>(
      this.apiUrl + 'Tunnel/financing/legal-information/' + input.tunnelConfigurationId,
      input
    );
  }

  private loadPricingFromInstallation(input: StorageInstallationDto) {
    let withInstallationKey: string = input.withInstallation ? 'withInstallation' : 'withoutInstallation';
    this.tunnelDataStore.updateCurrentPricing({
      totalConception: this.toPrice(
        (input.conceptionPrices[withInstallationKey] as StorageTunnelConceptionDetailPrice).totalPrice
      ),
      delivery: this.toPrice(input.deliveryPrice),
      totalInstallation: this.toPrice(input.installationPrice.price),
      totalWithoutFunding: this.toPrice(
        (input.conceptionPrices[withInstallationKey] as StorageTunnelConceptionDetailPrice).totalPrice
      ),
      detailConception: this.getDetailConception(input),

      delta: this.toPrice(input.conceptionPrices.delta),
    });
  }

  private getDetailConception(input: StorageInstallationDto) {
    if (input.withInstallation) {
      return new Map<string, Price>([
        ['productsPrice', this.toPrice(input.conceptionPrices.withInstallation.productsPrice)],
      ]);
    } else {
      return new Map<string, Price>([
        // Spread les entrées supplémentaires correctement
        ...Object.entries(input.conceptionPrices.categoryTotals).map(([key, value]): [string, Price] => [
          key,
          this.toPrice(value),
        ]),
      ]);
    }
  }

  private async updateStoragePricingFromKitchenTunnelPricing(input: StorageTunnelPricingDto) {
    const featureSettings = await this.featureSettingsService.getSettings();

    if (featureSettings.pose) {
      this.updateStorageInstallation(input);
    } else {
      this.tunnelDataStore.disableCurrentInstallation();
    }

    if (featureSettings.pose && this.currentInstallation?.withInstallation && input.conceptionPrices?.withInstallation) {
      this.computeStoragePricingWithInstallation(input);
    } else if (input.conceptionPrices?.withoutInstallation) {
      this.computeStoragePricingWithoutInstallation(input);
    }

    if (featureSettings.financement) {
      await this.loadStorageFunding(input);
    } else {
      this.tunnelDataStore.disableCurrentFunding();
    }
    if (typeof input.discountEnum === 'number') {
      this.tunnelDataStore.updateCurrentDiscount(this.discountValueToDiscountEnum(input.discountEnum as number));
    } else {
      this.tunnelDataStore.updateCurrentDiscount(input.discountEnum);
    }
  }

  public async computeFidelityPoint(input: GetCustomerFidelityInfoNouveauPointInput) {
    if (!(await this.fidelityEnabled())) return;
    try {
      this.tunnelDataStore.setFidelityLoading(true);

      const [withInstallation, withoutInstallation] = await Promise.all([
        this.fidelityService.getCustomerFidelityCardInfoNouveauPoint(this.buildFidelityWithInstallationInput(input)),
        this.fidelityService.getCustomerFidelityCardInfoNouveauPoint(this.buildFidelityWithoutInstallationInput(input)),
      ]);

      this.tunnelDataStore.updateCurrentPricing({
        ...this.currentPricing,
        fidelity: [withInstallation, withoutInstallation],
      });
    } catch (e) {
      logError(e);
    } finally {
      this.tunnelDataStore.setFidelityLoading(false);
    }
  }

  private buildFidelityWithInstallationInput(
    input: GetCustomerFidelityInfoNouveauPointInput
  ): GetCustomerFidelityInfoNouveauPointInput {
    let otherTotal =
      this.currentFunding?.fundingWithInstallation?.total > 0
        ? this.currentFunding?.fundingWithInstallation?.total
        : this.currentPricing?.totalWithFunding + this.currentPricing?.delta.actualAmount;
    return {
      ...input,
      amount: this.currentInstallation?.withInstallation ? Math.floor(input.amount) : Math.floor(otherTotal),
    };
  }

  private buildFidelityWithoutInstallationInput(
    input: GetCustomerFidelityInfoNouveauPointInput
  ): GetCustomerFidelityInfoNouveauPointInput {
    let otherTotal =
      this.currentFunding?.fundingWithoutInstallation.total > 0
        ? this.currentFunding?.fundingWithoutInstallation?.total
        : this.currentPricing?.totalWithFunding - this.currentPricing?.delta.actualAmount;
    return {
      ...input,
      amount: !this.currentInstallation?.withInstallation ? Math.floor(input.amount) : Math.floor(otherTotal),
    };
  }

  private computeStoragePricingWithInstallation(result: StorageTunnelPricingDto) {
    this.baseComputeStoragePricing(result);
    this.tunnelDataStore.updateCurrentPricing({
      detailConception: new Map<string, Price>([
        ['productsPrice', this.toPrice(result.conceptionPrices?.withInstallation?.productsPrice)],
      ]),
      totalConception: this.toPrice(result.conceptionPrices?.withInstallation?.productsPrice),
    });
  }

  private computeStoragePricingWithoutInstallation(result: StorageTunnelPricingDto) {
    this.baseComputeStoragePricing(result);
    this.tunnelDataStore.updateCurrentPricing({
      detailConception: new Map<string, Price>([
        ['productsPrice', this.toPrice(result.conceptionPrices?.withoutInstallation?.productsPrice)],
      ]),
      totalConception: this.toPrice(result.conceptionPrices?.withoutInstallation?.productsPrice),
    });
  }

  private baseComputeStoragePricing(result: StorageTunnelPricingDto) {
    const installationDisabled = !this.featureSettingsService.poseEnabled;
    const totalWithoutFunding = installationDisabled
      ? result.conceptionPrices?.withoutInstallation?.totalPrice ??
        result.conceptionPrices?.withoutInstallation?.productsPrice ??
        result.totalWithoutFinancing
      : result.totalWithoutFinancing;

    this.tunnelDataStore.updateCurrentPricing({
      delivery: this.toPrice(result.deliveryPrice),
      totalInstallation: installationDisabled ? this.toPrice(0) : this.toPrice(result.installationPrices?.price),
      totalWithoutFunding: this.toPrice(totalWithoutFunding),
      delta: installationDisabled ? this.toPrice(0) : this.toPrice(result.conceptionPrices?.delta),
      totalWithFunding: result.totalWithFinancing,
    });
  }

  private updateStorageInstallation(result: StorageTunnelPricingDto) {
    let detailInstallation = null;
    if (result.installationPrices?.installationDetails != undefined) {
      detailInstallation = JSON.parse(result.installationPrices?.installationDetails);
    }

    this.tunnelDataStore.updateCurrentInstallation({
      more2Years: result.houseMoreThan2YearsOld,
      withInstallation: result.withInstallation,
      detailInstallation: detailInstallation,
    });
  }

  private async loadStorageFunding(result: StorageTunnelPricingDto) {
    this.tunnelDataStore.updateCurrentFunding({
      ...this.mapStorageTunnelPricingFundingState(result),
    });
  }

  private mapStorageTunnelPricingFundingState(input: StorageTunnelPricingDto): FundingState {
    let fundingWithInstallation = this.buildEmptyFunding();
    let fundingWithoutInstallation = this.buildEmptyFunding();

    if (input.financing !== undefined) {
      fundingWithoutInstallation = {
        code: input.financing.select_bareme,
        duration: input.financing.select_duration,
        offerTable: this.mapRawOffersLiteOfferResults(JSON.parse(input.financing.offers)),
        ...this.getDataFromFundingOfferTable(
          input.financing.select_bareme,
          input.financing.select_duration,
          this.mapRawOffersLiteOfferResults(JSON.parse(input.financing.offers))
        ),
      };
    }

    if (input.financingWithInstallation !== undefined) {
      fundingWithInstallation = {
        code: input.financingWithInstallation.select_bareme,
        duration: input.financingWithInstallation.select_duration,
        offerTable: this.mapRawOffersLiteOfferResults(JSON.parse(input.financingWithInstallation.offers)),
        ...this.getDataFromFundingOfferTable(
          input.financingWithInstallation.select_bareme,
          input.financingWithInstallation.select_duration,
          this.mapRawOffersLiteOfferResults(JSON.parse(input.financingWithInstallation.offers))
        ),
      };
    }
    return {
      downPayment: input.downPayment,
      fundingWithInstallation: fundingWithInstallation,
      fundingWithoutInstallation: fundingWithoutInstallation,
    };
  }

  private async initStorageInspiration(configurationId: string): Promise<StorageInspiration | null> {
    if (!configurationId) return null;

    try {
      const storageInspirationData = await this.getInspiration({
        tunnelConfigurationId: configurationId,
      });

      if (!storageInspirationData) {
        return null;
      }

      const storageInspirationDto = Object.assign(new StorageInspirationDto(), storageInspirationData);
      const storageInspiration = storageInspirationDto.toStorageInspiration();

      if (!storageInspiration) {
        return null;
      }

      this.updateStorageProperties(storageInspiration);

      return storageInspiration;
    } catch (error) {
      logError("Erreur lors de l'initialisation de StorageInspiration:", error);
      throw error;
    }
  }

  private updateStorageProperties(storageInspiration: StorageInspiration): void {
    const propertiesToUpdate = ['inspirationId', 'boxId', 'frontId', 'handlesId', 'legsId'] as const;

    propertiesToUpdate.forEach(property => {
      if (storageInspiration[property] !== undefined) {
        this.current.storage[property] = storageInspiration[property];
      }
    });
  }

  private getInstallationData(input: GetInstallationDataInput): Promise<StorageInstallationDto> {
    return this.postHbm<{ houseMoreThan2YearsOld: boolean }, StorageInstallationDto>(
      this.apiUrl + 'Tunnel/installation/' + input.tunnelConfigurationId,
      undefined,
      { houseMoreThan2YearsOld: input.houseMoreThan2YearsOld }
    );
  }

  private getFundingData(input: GetFundingDataInput): Promise<StorageTunnelPricingDto> {
    return this.postHbm<GetFundingDataInput, StorageTunnelPricingDto>(
      this.apiUrl + 'Tunnel/financing/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  //quand tu changes le withInstallation
  private updateInstallation(input: UpdateInstallationInput): Promise<void> {
    return this.putHbm<TunnelUpdateInstallation, void>(
      this.apiUrl + 'Tunnel/installation/' + input.tunnelConfigurationId,
      input.installationUpdate
    );
  }

  //quand tu changes le discount
  private applyDiscount(input: ApplyDiscountInput): Promise<StorageTunnelPricingDto> {
    return this.postHbm<ApplyDiscountInput, StorageTunnelPricingDto>(
      this.apiUrl + 'Tunnel/discount/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  private getStorageTunnelByUUID(input: GetTunnelByHBMUUIDInput): Promise<BaseStorageConfigurationDto> {
    return this.getHbm<BaseStorageConfigurationDto>(this.apiUrl + 'Tunnel/hbm/' + input.hbmUUID);
  }

  private getInspiration(input: GetInspirationInput): Promise<StorageInspirationDto> {
    return this.postHbm<GetInspirationInput, StorageInspirationDto>(
      this.apiUrl + 'Tunnel/inspiration/' + input.tunnelConfigurationId
    );
  }

  private getPricing(input: GetPricingInput): Promise<StorageTunnelPricingDto> {
    return this.postHbm<GetPricingInput, StorageTunnelPricingDto>(
      this.apiUrl + 'Tunnel/pricing/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  private deleteTunnel(input: DeleteTunnelInput): Promise<BaseStorageConfigurationDto> {
    return this.deleteHbm<DeleteTunnelInput, BaseStorageConfigurationDto>(
      this.apiUrl + 'Tunnel/delete/' + input.tunnelConfigurationId
    );
  }

  private updateStorageTunnel(input: StorageCreateUpdateTunnel): Promise<StorageConfiguration> {
    return this.putHbm<StorageCreateUpdateTunnel, StorageConfiguration>(
      this.apiUrl + 'Tunnel/update/' + input.tunnelConfigurationId,
      input
    );
  }

  private duplicateHbmTunnel(input: DuplicateTunnelHBMInput): Promise<BaseStorageConfigurationDto> {
    return this.postHbm<DuplicateTunnelHBMInput, BaseStorageConfigurationDto>(
      this.apiUrl + 'Tunnel/duplicate/hbm',
      undefined,
      input
    );
  }

  private createStorageTunnel(input: StorageCreateUpdateTunnel): Promise<StorageConfiguration> {
    return this.postHbm<StorageCreateUpdateTunnel, StorageConfiguration>(this.apiUrl + 'Tunnel/create', input);
  }

  public async generateStoragePdf(input: HbmPdfViewModel) {
    return await this.getStoragePdf(input);
  }

  public async getStorageMedias(HBMConfigurationId: string): Promise<HBMMedias> {
    let storageMedias: HBMMedias = new HBMMedias();
    try {
      let projectData: any = await this.projectService.getDetails(HBMConfigurationId);
      projectData.medias.thumbnail.map(thumbnail => {
        let p: HBMMedia = {
          name: thumbnail.name,
          url: thumbnail.url,
        };
        storageMedias.thumbnail.push(p);
      });

      projectData.medias.plan2D.map(plan2D => {
        let p: HBMMedia = {
          name: plan2D.name,
          url: plan2D.url,
        };
        storageMedias.plan2D.push(p);
      });
    } catch (error) {
      logError(error);
    }
    return storageMedias;
  }

  private getStoragePdf(input: HbmPdfViewModel) {
    return this.postHbm<HbmPdfViewModel, any>(this.baseApiUrl + 'Pdf/GeneratePdf/storage/', input, {
      tunnelConfigurationId: input.tunnelConfigurationId,
    });
  }

  postConfiguration(input: any): Promise<any> {
    return this.post(this.apiUrl + 'configuration', input);
  }

  getConfigurationByProjectUUID(input: any): Promise<any> {
    return this.post(this.apiUrl + 'configuration/project', input);
  }

  override duplicateProjectById(id: string, title: string): Promise<any> {
    const params = new HttpParams().set('id', id).set('title', title);
    return this.post(`${this.apiUrl}configuration/duplicate`, {}, params);
  }

  async postOffres(input: any): Promise<any> {
    if (!(await this.featureSettingsService.isFinancementEnabled())) {
      this.tunnelDataStore.disableCurrentFunding();
      return undefined;
    }

    try {
      const res = await this.post(this.apiUrl + 'financement/offres', input);
      return res.data;
    } catch {
      return undefined;
    }
  }

  async postMentionsLegale(input: any): Promise<any> {
    if (!(await this.featureSettingsService.isFinancementEnabled())) {
      this.tunnelDataStore.disableCurrentFunding();
      return undefined;
    }

    try {
      const res = await this.post(this.apiUrl + 'financement/mentionslegales', input);
      return res.data;
    } catch {
      return undefined;
    }
  }
}
